import React, { Component, createRef } from 'react';
import classNames from 'classnames';
import { omit } from 'lodash';
import withFontClassName from '../../hoc/with-font-class-name';
import ButtonTooltip from '../button-tooltip';
import styles from './button.scss';

const SHOW_TIMEOUT = 200;
const HIDE_TIMEOUT = 3000;

export type ButtonProps = {
  component?: React.ElementType;
  className?: string;
  contentFontClassName?: string;
  /** One of the `Button.TYPE_*` statics, or a list of them. */
  type?: string | string[];
  isSecondary?: boolean;
  isSubmit?: boolean;
  isReset?: boolean;
  isMuted?: boolean;
  children?: React.ReactNode;
  tooltipText?: string;
  useFixedColor?: boolean;
  keepFocusStyles?: boolean;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> & {
    [dataAttribute: `data-${string}`]: unknown;
  };

type State = {
  isTooltipVisible: boolean;
};

class Button extends Component<ButtonProps, State> {
  static TYPE_FULL_WIDTH = styles.fullWidth;

  static TYPE_FULL_WIDTH_MOBILE = styles.fullWidthMobile;

  static TYPE_SMALL_SPACINGS_MOBILE = styles.smallSpacingsMobile;

  static TYPE_LARGE = styles.large;

  timeout?: ReturnType<typeof setTimeout>;

  state: State = {
    isTooltipVisible: false,
  };

  buttonRef = createRef<HTMLElement>();

  componentWillUnmount() {
    this.hideTooltip();
  }

  showTooltip() {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.setState({ isTooltipVisible: true }, () => {
        this.timeout = setTimeout(() => this.hideTooltip(), HIDE_TIMEOUT);
      });
    }, SHOW_TIMEOUT);
  }

  hideTooltip() {
    clearTimeout(this.timeout);
    this.setState({
      isTooltipVisible: false,
    });
  }

  handleMouseEnter = () => {
    this.showTooltip();
  };

  handleMouseLeave = () => {
    this.hideTooltip();
  };

  renderTooltip() {
    const { isTooltipVisible } = this.state;
    const { tooltipText } = this.props;

    if (!tooltipText) {
      return null;
    }

    return (
      <ButtonTooltip target={this.buttonRef.current} isVisible={isTooltipVisible}>
        {tooltipText}
      </ButtonTooltip>
    );
  }

  render() {
    const {
      component: Component = 'button',
      className,
      contentFontClassName,
      isSecondary,
      isSubmit,
      isReset,
      isMuted,
      children,
      type,
      useFixedColor,
      keepFocusStyles,
      ...otherProps
    } = this.props;

    const isPrimary = !isSecondary;
    let colorsClassName;

    if (useFixedColor) {
      colorsClassName = isPrimary ? styles.fixedPrimary : styles.fixedSecondary;
    } else {
      colorsClassName = classNames(
        isPrimary ? 'blog-button-background-color' : 'blog-button-border-color',
        isPrimary ? 'blog-button-primary-text-color' : 'blog-button-secondary-text-color',
      );
    }

    const containerClassName = classNames(
      keepFocusStyles && styles.clipboardFocusRing,
      styles.button,
      className,
      contentFontClassName,
      colorsClassName,
      type,
      {
        [styles.secondary]: isSecondary,
        [styles.isMuted]: isMuted,
      },
    );
    const componentProps = omit(
      otherProps,
      'isSubmit',
      'currentUser',
      'isBlocked',
      'dispatch',
      'viewMode',
      'isDemoMode',
      'openModal',
      'isAuthenticated',
      'titleFontClassName',
      'contentFontClassNameWithStyle',
      'metadataFontClassName',
      'tooltipText',
      'isPrivate',
    );

    return (
      <Component
        className={containerClassName}
        type={(isSubmit && 'submit') || (isReset && 'reset') || 'button'}
        {...componentProps}
        onMouseEnter={this.handleMouseEnter}
        onMouseMove={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        {this.renderTooltip()}
        <span className={styles.wrapper}>{children}</span>
      </Component>
    );
  }
}

export default withFontClassName(Button);
